import { Archive as ArchiveIcon, Database } from 'lucide-react';
import { ClientActivityWall } from '@/components/ClientActivityWall';
import { usePlayerActivity } from '@/hooks/usePlayerActivity';

export function Archive() {
  const activity = usePlayerActivity({ archive: true, limit: 300 });
  const items = activity.data?.items ?? [];

  return (
    <section className="space-y-5">
      <div className="surface overflow-hidden rounded-control">
        <div className="flex flex-col gap-5 border-b border-border/80 p-5 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-bartlettGold">
              Saved Activity
            </p>
            <h1 className="mt-2 text-2xl font-semibold tracking-normal">Archive</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted">
              Historical Bartlett notes and client-file additions after they roll out of the current seven-day People view.
            </p>
          </div>
          <div className="grid gap-2 sm:min-w-[360px] sm:grid-cols-2">
            <Metric icon={ArchiveIcon} label="Prior entries" value={String(activity.data?.total ?? items.length)} />
            <Metric icon={Database} label="Current window" value={formatWindow(activity.data?.startAt)} />
          </div>
        </div>
      </div>

      {activity.isLoading ? (
        <ArchiveSkeleton />
      ) : activity.isError ? (
        <div className="rounded-control border border-flag-critical/30 bg-flag-critical/5 p-4 text-sm text-flag-critical">
          Couldn&apos;t load archived activity.
        </div>
      ) : (
        <ClientActivityWall items={items} empty="No prior Bartlett profile activity is older than the current seven-day window yet." />
      )}
    </section>
  );
}

function formatWindow(value: number | undefined): string {
  if (!value) return '—';
  return new Date(value * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function Metric({ icon: Icon, label, value }: { icon: typeof Database; label: string; value: string }) {
  return (
    <div className="rounded-control border border-border bg-white px-3 py-2">
      <div className="flex items-center gap-2 text-muted">
        <Icon size={14} />
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em]">{label}</span>
      </div>
      <div className="mt-1 truncate font-mono text-lg font-semibold text-ink">{value}</div>
    </div>
  );
}

function ArchiveSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((item) => (
        <div key={item} className="h-24 animate-pulse rounded-control border border-border bg-white" />
      ))}
    </div>
  );
}
